import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    CHAT_ENTITY_TYPE_APPOINTMENT,
    FILE_MESSAGE_TEXT_CONTENT,
    getUserTxRole,
    handleOtherPartyForDeletedListing,
    organizeItems,
    resolveOtherPartyData,
} from '../../../util/transaction';
import { anonymizeOtherParty } from '../../../util/anonym';
import { createResourceLocatorString } from '../../../util/routes';
import { ensureUser } from '../../../util/data';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { Avatar, Tooltip, UserDisplayName } from '../../../components';
import { TOOLTIP_DATA_REQUEST_ATTR } from '../../../components/Tooltip/Tooltip';
import { resolveTransitionMessage } from '../../../components/TransitionMessage/resolveTransitionMessage';
import { chatItemFabric } from '../../../components/ActivityFeed/ActivityFeed.helpers';
import { archieveChat } from '../../InboxPage/InboxPage.duck';
import routeConfiguration from '../../../routeConfiguration';

import { hasNewMessage, hasNewTransition } from '../InboxPanel.helpers';
import { ARCHIEVED_FILTER_KEY, FILTER_SEARCH_PARAM } from './Filters';
import { formatDate, getFormattedDate, txState } from './InboxItem.helpers';
import BlockUserModal from './BlockUserModal';
import css from './InboxItem.css';
import { useDearchieveChatOnNewMessage } from './useDearchieveChatOnNewMessage';
import { IconArchieveL, IconBlockL, IconPresentL } from '../../../icons';
import { parse } from '../../../util/urlHelpers';
import { resolveFileMessageType } from '../../../util/transaction-messages';

export const InboxItem = ({
    tx,
    currentUser,
    allUserMessages,
    setInboxPanelVisible,
    activeArchivePanelId,
    setActiveArchivePanelId,
    isSelected,
    appointmentEntities: appointmentEntitiesData,
    panelRef,
    filters,
    intl,
}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const onArchieveChat = (txId, action) => dispatch(archieveChat(txId, action));

    const [isMobile, , computing] = useIsMobile(1024);
    const [isUnread, setIsUnread] = useState(false);
    const [actionRequired, setActionRequired] = useState(false);
    const [showItem, setShowItem] = useState(false);
    const [blockUserModalOpen, setBlockUserModalOpen] = useState(false);

    const { search } = history.location;
    const searchParsed = parse(search);

    const currentListing = tx.listing;

    const currentProvider = ensureUser(tx.provider);
    const currentCustomer = ensureUser(tx.customer);
    const otherParty = handleOtherPartyForDeletedListing(
        anonymizeOtherParty(
            resolveOtherPartyData(currentUser, currentCustomer, currentProvider),
            currentUser,
            currentListing
        ),
        currentListing
    );

    const { archievedTransactions = {} } = currentUser.attributes.profile.protectedData;

    const {
        attributes: {
            lastTransition,
            transitions,
            protectedData: { isSponsored },
        },
        id: { uuid },
    } = tx;

    const isArchived = archievedTransactions[uuid];
    const hasFilters = filters && filters.length > 0;
    const hasArchieveFilterEnabled = hasFilters && filters.includes(ARCHIEVED_FILTER_KEY);

    const unreadMessages = allUserMessages[uuid];
    const appointmentEntities = appointmentEntitiesData[uuid] || [];

    useDearchieveChatOnNewMessage({
        currentUser,
        tx,
        hasArchieveFilterEnabled,
        unreadMessages,
        isSelected,
        setShowItem,
    });

    useEffect(() => {
        /** mark tx item as the one which has unread message or tx action */
        setIsUnread(hasNewMessage(tx, allUserMessages, currentUser));
        setActionRequired(hasNewTransition(tx, currentUser));
    }, [currentUser, allUserMessages, tx]);

    const isAppointmentInitialMessage = ({ data, type }) =>
        data && data.message && type === CHAT_ENTITY_TYPE_APPOINTMENT;

    const organizedItems = organizeItems(
        allUserMessages[uuid] || [],
        transitions.find(t => t.transition === lastTransition),
        appointmentEntities.filter(
            ({ transition, data, type }) =>
                !!transition || isAppointmentInitialMessage({ data, type })
        )
    );

    const stateData = txState(tx);
    const ownRole = getUserTxRole(currentUser.id, tx);
    const lastMessage = organizedItems[organizedItems.length - 1];

    otherParty.attributes.profile.displayName =
        otherParty.attributes.profile.displayName &&
        otherParty.attributes.profile.displayName
            .split(' ')
            .splice(0, 1)
            .join('');

    const otherUserDisplayName = (
        <UserDisplayName
            user={otherParty}
            shouldBeTrimmed={otherParty.attributes.profile.isAnonym}
        />
    );
    const isOtherUserBanned = otherParty.attributes.banned;

    const lastTransitionedAtFormatted = chatItemFabric({
        item: lastMessage,
        messageComponent: ({ attributes: { createdAt } }) => formatDate(new Date(createdAt)),
        transitionComponent: ({ createdAt }) => formatDate(new Date(createdAt)),
        appointmentComponent: ({ createdAt }) => formatDate(new Date(createdAt)),
        shareContactInfoComponent: ({ attributes: { createdAt } }) =>
            formatDate(new Date(createdAt)),
    });

    const linkClasses = classNames(css.itemLink, {
        [css.bannedUserLink]: isOtherUserBanned,
    });

    const archivePanelOpen = activeArchivePanelId === uuid;

    const redirectTo = (pageName, params = {}) => {
        const redirectSearchParams = { ...searchParsed };

        if (hasFilters) {
            redirectSearchParams[FILTER_SEARCH_PARAM] = filters.join(encodeURIComponent(','));
        }

        return history.replace(
            createResourceLocatorString(
                pageName,
                routeConfiguration(),
                { ...params },
                redirectSearchParams
            )
        );
    };

    const isFile = message => {
        try {
            const { attributes } = message || {};
            const { content } = attributes || {};
            const { fileIsDoc, fileIsImage } = resolveFileMessageType(message);
            if (fileIsImage) return 'Foto';
            if (fileIsDoc) return 'Dokument';

            return content === FILE_MESSAGE_TEXT_CONTENT ? 'Datei' : content;
        } catch (e) {
            return 'Neue Nachricht';
        }
    };

    /** SUB-COMPONENT */
    const UserAvatar = () => (
        <div className={css.itemAvatar}>
            <Avatar user={otherParty} />
        </div>
    );
    /** SUB-COMPONENT */
    const NewMessageBadge = () => (isUnread ? <p className={css.newActionBadge}>Neu</p> : null);
    /** SUB-COMPONENT */
    const UserName = () => (
        <p className={css.itemUsername}>
            {otherUserDisplayName} <NewMessageBadge />
        </p>
    );
    /** SUB-COMPONENT */
    const BookingMessage = () => (
        <p
            className={classNames(css.bookingNoActionNeeded, {
                [css.bookingActionNeeded]: isUnread,
            })}
        >
            {lastMessage &&
                chatItemFabric({
                    item: lastMessage,
                    shareContactInfoComponent: () => 'Mobilnummer',
                    messageComponent: isFile,
                    transitionComponent: transition =>
                        resolveTransitionMessage({
                            currentTransaction: tx,
                            ownRole,
                            transition,
                            intl,
                            currentUser,
                            suppressLinks: true,
                            currentListing,
                        }),
                    appointmentComponent: appointment =>
                        isAppointmentInitialMessage(appointment || {})
                            ? intl.formatMessage({
                                  id: 'InboxPage.appointmentSent',
                              })
                            : appointment && !appointment.transition
                            ? resolveTransitionMessage({
                                  currentTransaction: tx,
                                  ownRole,
                                  intl,
                                  currentUser,
                                  ...appointment,
                                  by: appointment.sender.userRole,
                                  currentListing,
                              })
                            : null,
                })}
        </p>
    );
    /** SUB-COMPONENT */
    const BookingStatusBadge = () => (
        <div className={classNames(css.stateName, stateData.stateClassName)}>
            {intl.formatMessage({
                id: stateData.state,
            })}
        </div>
    );
    /** SUB-COMPONENT */
    const LastTransitionInfo = () => (
        <p
            className={css.lastTransitionedAt}
            title={getFormattedDate(lastTransitionedAtFormatted.short)}
        >
            {lastTransitionedAtFormatted.long}
        </p>
    );
    /** SUB-COMPONENT */
    const ControlsPanel = () => (
        <Tooltip
            isStatic
            staticTooltipState={archivePanelOpen}
            outsideClickHandler={() => setActiveArchivePanelId(null)}
            staticTooltipStateHandler={() => setActiveArchivePanelId(null)}
            tooltipClassName={classNames(css.controls, {
                [css.isArchived]: isArchived,
            })}
            rootClassName={css.tooltipWrapper}
            parentContainerRef={panelRef}
            content={
                <ul>
                    <li
                        onClick={e => {
                            e.stopPropagation();
                            onArchieveChat(uuid, isArchived ? 'remove' : 'add').then(() =>
                                redirectTo('OrderMessagesPage')
                            );
                        }}
                    >
                        <IconArchieveL />
                        {isArchived ? 'Wiederherstellen' : 'Archivieren'}
                    </li>
                    <li
                        onClick={e => {
                            e.stopPropagation();
                            setActiveArchivePanelId(null);
                            setBlockUserModalOpen(true);
                        }}
                    >
                        <IconBlockL />
                        Blockieren
                    </li>
                </ul>
            }
        >
            <aside
                onClick={e => {
                    e.stopPropagation();
                    setActiveArchivePanelId(archivePanelOpen ? null : uuid);
                }}
                className={css.archieveLink}
            >
                <span className={css.ellipsis} data-role={TOOLTIP_DATA_REQUEST_ATTR}>
                    ...
                </span>
            </aside>
        </Tooltip>
    );

    return showItem && !computing ? (
        <>
            <li
                key={uuid}
                className={classNames(css.listItem, {
                    [css.isSelected]: isSelected || actionRequired || isUnread,
                })}
            >
                {isSponsored && (
                    <div className={css.roundedBadge}>
                        <IconPresentL />
                    </div>
                )}
                <div
                    className={css.item}
                    onClick={() => {
                        isMobile && setInboxPanelVisible(false);
                    }}
                >
                    <UserAvatar />

                    <div
                        className={linkClasses}
                        onClick={() => redirectTo('OrderDetailsPage', { id: tx.id.uuid })}
                    >
                        {isMobile ? (
                            <>
                                <div className={css.itemInfoMob}>
                                    <UserName />
                                    <LastTransitionInfo />
                                </div>
                                <div className={css.itemInfo}>
                                    <BookingMessage />
                                </div>
                                <div className={css.itemStateMob}>
                                    <NewMessageBadge />
                                    <BookingStatusBadge />
                                    <ControlsPanel />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={css.itemInfo}>
                                    <UserName />
                                    <BookingMessage />
                                </div>
                                <div className={css.itemState}>
                                    <BookingStatusBadge />
                                    <LastTransitionInfo />
                                </div>
                            </>
                        )}
                    </div>

                    {!isMobile && <ControlsPanel />}
                </div>
            </li>
            {blockUserModalOpen && (
                <BlockUserModal
                    history={history}
                    otherParty={otherParty}
                    redirectTo={redirectTo}
                    setBlockUserModalOpen={setBlockUserModalOpen}
                />
            )}
        </>
    ) : null;
};
